import React, { ReactElement, ReactNode, useState } from 'react';
import styled from 'styled-components';
import { border } from 'styled-system';

import { Flex, ButtonIcon, SearchInput, SearchSelect, FlexProps, Button, Hr, Typography } from 'components';
import { IconsTypes } from 'components/icons';

const CustomButtonIcon = styled((props) => <ButtonIcon {...props} />)`
  flex-shrink: 0;
`;

const ButtonGroup = styled((props: FlexProps) => <Flex {...props} />)`
  flex-wrap: nowrap;
  border: 2px solid ${({ theme }) => theme.colors.gray_200};
  border-radius: 4px;

  &:first-child {
    ${CustomButtonIcon} {
      border-top-left-radius: 2px;
      border-bottom-left-radius: 2px;
    }
  }

  &:last-child {
    ${CustomButtonIcon} {
      border-top-right-radius: 2px;
      border-bottom-right-radius: 2px;
    }
  }

  ${border}
`;

const MoreFilterWrapper = styled.div`
  display: flex;
  position: absolute;
  top: calc(100% + 10px);
  right: 0;
  background: white;
  z-index: 100;
  border: 2px solid ${({ theme }) => theme.colors.gray_200};
  border-radius: 4px;
  min-width: 100px;
  flex-direction: column;

  &::before {
    content: '';
    width: 0;
    height: 0;
    border-left: 7px solid transparent;
    border-right: 7px solid transparent;
    border-bottom: 7px solid ${({ theme }) => theme.colors.gray_200};
    position: absolute;
    right: 18px;
    top: -7px;
    z-index: 99;
  }

  &::after {
    content: '';
    width: 0;
    height: 0;
    border-left: 5px solid transparent;
    border-right: 5px solid transparent;
    border-bottom: 5px solid white;
    position: absolute;
    right: 20px;
    top: -4px;
    z-index: 99;
  }
`;

interface SearchFilterProps {
  selectedValue: string | number;
  placeholder?: string;
  onChange: (value: string | number) => void;
  options: {
    label: string | number;
    value: string | number;
  }[];
  minWidth?: number;
  defaultValue?: number | string;
}

interface ActionsProps {
  icon: IconsTypes;
  label: string;
  onClick: () => void;
  disabled?: boolean;
  active?: boolean;
}

interface SearchBarProps {
  searchText: string;
  searchPlaceholder?: string;
  onSearchTextChange: (text: string) => void;
  onSearchSubmit?: (event?: any) => void;
  hideSearch?: boolean;
  currentPage: number;
  totalPages: number;
  setPage?: (page: number) => void;
  filters?: SearchFilterProps[];
  actions?: ActionsProps[];
  more?: ReactNode;
  selectedRows?: boolean;
}

const SearchBar = ({
  searchText,
  searchPlaceholder = 'Buscar por nome',
  onSearchTextChange,
  onSearchSubmit,
  hideSearch = false,
  setPage,
  currentPage,
  totalPages,
  actions,
  filters,
  more,
  selectedRows = false,
}: SearchBarProps): ReactElement => {
  const [searchTextTemp, setSearchTextCenter] = useState(searchText);
  const [showMore, setShowMore] = useState(false);

  const inputRef = React.useRef<HTMLInputElement>(null);

  return (
    <Flex flexGrow={1} flexWrap="nowrap" flexDirection={{ m: 'column', t: 'row' }}>
      <Flex flexGrow={1} position="relative" minHeight={55}>
        {!hideSearch && (
          <>
            <CustomButtonIcon
              icon="search"
              iconWidth={20}
              bg="transparent"
              color={searchTextTemp ? 'primary_400' : 'gray_500'}
              position="absolute"
              top={18}
              right={18}
              disabled={!searchTextTemp}
              onClick={() => {
                onSearchTextChange(searchTextTemp);
              }}
            />

            {Boolean(searchTextTemp) && (
              <CustomButtonIcon
                icon="close"
                width={38}
                height={35}
                iconWidth={22}
                bg="transparent"
                color={searchTextTemp ? 'primary_400' : 'gray_500'}
                position="absolute"
                top="50%"
                style={{ transform: 'translateY(-50%)' }}
                right={50}
                disabled={!searchTextTemp}
                onClick={() => {
                  setSearchTextCenter('');
                  onSearchTextChange('');
                  if (onSearchSubmit) onSearchSubmit();

                  inputRef.current?.focus();
                }}
                borderRight="2px solid"
                borderRightColor="gray_200"
              />
            )}

            <SearchInput
              type="text"
              data-testid="search-input"
              placeholder={searchPlaceholder}
              value={searchTextTemp}
              ref={inputRef}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => setSearchTextCenter(event.target.value)}
              onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
                if (event.key === 'Enter') {
                  onSearchTextChange(searchTextTemp);
                  // onSearchSubmit(event);
                }
              }}
            />
          </>
        )}
      </Flex>

      {filters?.map((filter, filterIndex) => (
        <Flex
          flexGrow={{ m: 1, t: 0 }}
          key={`filterItem-${filterIndex.toString()}`}
          ml={{ m: 0, t: 2 }}
          mt={{ m: 2, t: 0 }}
          mb={{ m: 2, t: 0 }}
          height={{ m: 55 }}
          minWidth={filter.minWidth || 200}
        >
          <SearchSelect
            defaultValue={filter.defaultValue || undefined!}
            onChange={(event: React.ChangeEvent<HTMLSelectElement>) => {
              const { value: filterValue } = event.target;
              if (filterValue) {
                filter.onChange(filterValue);
                if (onSearchSubmit) onSearchSubmit();
              }
            }}
          >
            {!!filter.placeholder && <option value="">{filter.placeholder}</option>}
            {filter.options.map((filterOption, indexFilterOption) => (
              <option
                selected={filter.selectedValue === filterOption.value}
                key={`filterOptionItem-${filterIndex.toString()}-${indexFilterOption.toString()}`}
                value={filterOption.value}
              >
                {filterOption.label}
              </option>
            ))}
          </SearchSelect>
        </Flex>
      ))}

      <Flex flexGrow={0} justifyContent="space-between" flexWrap="nowrap">
        {!!actions && !!actions.length && (
          <Flex flexGrow={0} height={{ m: 50, t: 'inherit' }} ml={{ m: 0, t: 2 }}>
            <ButtonGroup>
              {actions.map((action, index) => (
                <Flex key={`searchbar-action-${index.toString()}`} borderLeft={index ? '2px solid' : ''} borderColor="gray_200">
                  <CustomButtonIcon
                    icon={action.icon}
                    width={50}
                    iconWidth={20}
                    bg={action.active === true ? 'white' : action.active === false ? 'gray_100' : 'gray_100'}
                    bgHover={action.active === true ? 'white' : action.active === false ? 'white' : 'gray_100'}
                    color={action.disabled ? 'gray_300' : action.active === true ? 'primary_400' : action.active === false ? 'gray_300' : 'gray_300'}
                    colorHover={
                      action.disabled ? 'gray_300' : action.active === true ? 'primary_400' : action.active === false ? 'primary_400' : 'gray_300'
                    }
                    justifyContent="center"
                    onClick={action.onClick}
                    disabled={action.disabled || false}
                    title={action.label}
                  />
                </Flex>
              ))}
            </ButtonGroup>
          </Flex>
        )}

        {!!more && (
          <Flex flexGrow={0} ml={2} position="relative">
            <ButtonGroup borderColor={showMore ? 'primary_400' : 'gray_200'} style={{ position: 'relative', zIndex: 100 }}>
              <CustomButtonIcon
                icon="filter"
                width={50}
                iconWidth={20}
                bg={showMore ? 'primary_400' : 'gray_100'}
                bgHover={showMore ? 'primary_400' : 'white'}
                color={showMore ? 'white' : 'gray_300'}
                colorHover={showMore ? 'white' : 'primary_400'}
                justifyContent="center"
                onClick={() => setShowMore((prev) => !prev)}
                title="Mais filtros"
              />
            </ButtonGroup>

            {showMore && (
              <>
                <MoreFilterWrapper>
                  {more}

                  <Hr m={0} size="2px" />

                  <Flex justifyContent="center" py={3}>
                    <Button size="small" onClick={() => setShowMore((prev) => !prev)}>
                      Fechar
                    </Button>
                  </Flex>
                  {/* <Box
                  position="absolute"
                  bottom="-55px"
                  left="50%"
                  style={{ transform: 'translateX(-50%)' }}
                >
                  <Button size="small" onClick={() => setShowMore((prev) => !prev)}>
                    Fechar
                  </Button>
                </Box> */}
                </MoreFilterWrapper>
                <Flex position="fixed" top={0} left={0} width="100vw" height="100vh" bg="rgba(0, 0, 0, .1)" zIndex={10} />
              </>
            )}
          </Flex>
        )}

        {!!setPage && (
          <Flex ml={3}>
            <ButtonGroup>
              <Flex>
                <CustomButtonIcon
                  icon="chevronLeft"
                  width={50}
                  iconWidth={25}
                  bg="white"
                  bgHover="gray_100"
                  color={currentPage > 1 ? 'primary_400' : 'gray_300'}
                  justifyContent="center"
                  disabled={currentPage <= 1}
                  onClick={() => {
                    setPage(currentPage - 1);
                  }}
                />
              </Flex>

              <Flex bg="gray_100" borderLeft="2px solid" borderRight="2px solid" borderColor="gray_200" alignItems="center">
                <Typography px={3} type="type1" color="gray_500" style={{ whiteSpace: 'nowrap' }}>
                  {currentPage}
                  {' de '}
                  {totalPages}
                </Typography>
              </Flex>

              <Flex>
                <CustomButtonIcon
                  icon="chevronRight"
                  width={50}
                  iconWidth={25}
                  bg="white"
                  bgHover="gray_100"
                  color={currentPage < totalPages ? 'primary_400' : 'gray_300'}
                  justifyContent="center"
                  disabled={currentPage >= totalPages}
                  onClick={() => {
                    setPage(currentPage + 1);
                  }}
                />
              </Flex>
            </ButtonGroup>
          </Flex>
        )}
      </Flex>
    </Flex>
  );
};
export default SearchBar;
